import { useState } from "react";
import { useAuth } from "../state/AuthContext";

// Phase 2 brief, item 1: "a login screen supporting both paths — local
// username/password with a TOTP step, and an SSO button." Generic on
// purpose: Amalgamesh is released
// publicly and must work with any OIDC provider, not tied to one
// operator's own Authentik instance — no provider name appears anywhere in this file. The
// button's label and even its presence come entirely from `sso` (see
// AuthContext.tsx): OIDC is off by default, so when the backend hasn't
// configured a provider, no SSO button renders at all — the standing rule
// that the UI never offers what isn't there. This screen renders correctly
// in all three real states: local only, SSO only (would need a second
// screen state if local were ever disabled entirely — not the case today,
// since local+TOTP is the baseline that always works), and both together.
export function LoginScreen({ expired, setupMode }: { expired?: boolean; setupMode?: boolean }) {
  const { login, submitTotp, setupAdmin, sso, notice, passwordMinLength } = useAuth();
  const [step, setStep] = useState<"credentials" | "totp">("credentials");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (setupMode && password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    const res = setupMode ? await setupAdmin(username, password) : await login(username, password);
    setBusy(false);
    if (res.ok) return; // AuthContext flips `status` to "authenticated"; App re-renders past this screen
    if (res.totpRequired) {
      setPendingToken(res.pendingToken ?? null);
      setStep("totp");
      return;
    }
    setError(res.error ?? (setupMode ? "Couldn't create the admin account." : "Sign-in failed."));
  }

  async function onSubmitTotp(e: React.FormEvent) {
    e.preventDefault();
    if (!pendingToken) {
      setError("Session for the verification step expired — start over.");
      setStep("credentials");
      return;
    }
    setError(null);
    setBusy(true);
    const res = await submitTotp(pendingToken, code);
    setBusy(false);
    if (res.ok) return;
    setError(res.error ?? "Verification failed.");
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">AMALGAMESH</div>
        {/* `notice` (from AuthContext's verified auth-failure handling) is the
            honest, specific reason when one was determined — e.g. "the
            session cookie wasn't stored" is a real, different fault from
            "your session expired," and conflating them is what hid a real
            bug (found live: a user could log in but was bounced
            straight back with a generic expiry message that wasn't true).
            Only fall back to the generic expired copy if we don't have a
            more specific reason. */}
        {(expired || notice) && <div className="auth-notice">{notice ?? "Your session expired. Sign in again to continue."}</div>}
        {setupMode && (
          <div className="auth-notice">No admin account exists yet. Create the first one to continue.</div>
        )}

        {step === "credentials" ? (
          <form onSubmit={onSubmitCredentials}>
            <label className="auth-field">
              <span>Username</span>
              <input
                name="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
              />
            </label>
            <label className="auth-field">
              <span>Password</span>
              <div className="auth-password-row">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={setupMode ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={setupMode && passwordMinLength != null ? passwordMinLength : undefined}
                  required
                />
                <button
                  type="button"
                  className="auth-link-btn auth-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {setupMode && passwordMinLength != null && (
                <span className="auth-hint">At least {passwordMinLength} characters.</span>
              )}
            </label>
            {setupMode && (
              <label className="auth-field">
                <span>Confirm password</span>
                <input
                  name="confirm-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </label>
            )}
            {error && <div className="auth-error">{error}</div>}
            <button type="submit" className="auth-submit" disabled={busy}>
              {busy ? (setupMode ? "Creating…" : "Signing in…") : setupMode ? "Create admin account" : "Sign in"}
            </button>
          </form>
        ) : (
          <form onSubmit={onSubmitTotp}>
            <p className="auth-hint">Enter the 6-digit code from your authenticator app.</p>
            <label className="auth-field">
              <span>Verification code</span>
              <input
                name="totp"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                required
              />
            </label>
            {error && <div className="auth-error">{error}</div>}
            <button type="submit" className="auth-submit" disabled={busy}>
              {busy ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              className="auth-link-btn"
              onClick={() => {
                setStep("credentials");
                setError(null);
                setCode("");
              }}
            >
              Back
            </button>
          </form>
        )}

        {sso.available && !setupMode && (
          <>
            <div className="auth-divider">
              <span>or</span>
            </div>

            <a className="auth-oidc-btn" href={sso.loginPath}>
              {sso.label}
            </a>
          </>
        )}
      </div>
    </div>
  );
}
